import escapeRegExp from 'lodash/escapeRegExp';
import orderBy from 'lodash/orderBy';
import unset from 'lodash/unset';
import {
  type AppDate,
  dayjs,
  isValidTimezone,
  parseLenient,
  parseStrictIsoLocal,
  wallClockAtHour,
} from 'src/helper/date';
import type { CalendarObj } from 'src/redux/user-management/types';
import type {
  MaterialType,
  QuoteTaskOptionType,
  QuoteTaskType,
  QuoteType,
  QuoteWorkType,
  TaskOptionType,
  TripType,
  WorkOrderDataType,
  WorkOrderIssueType,
} from 'src/redux/vendor-portal/work-order/types';
import type {
  HealthScore,
  QuoteOption,
  TripSource,
  WorkOrderAPIResponseObj,
  WorkOrderTrips,
} from 'src/redux/work-order/types';
import {
  ISSUES_STATUS,
  ISSUES_STATUS_LABEL,
  MATERIAL_QUANTITY_TYPES,
  QUOTE_STATUS_KEYS,
  QUOTE_STATUS_LABELS,
  WO_STATUS_KEYS,
  WO_STATUS_LABELS,
} from 'src/views/VendorPortal/WorkOrder/constants';
import type { IssueMaterialType } from 'src/views/VendorPortal/WorkOrder/types';
import type { ServiceWindow } from './schedule-types';
import { SCHEDULE_SLOT_DEFINITIONS } from './schedule-types';
import type { ListObj, OptionType } from './shared-types';
import {
  calculateTimeDiff,
  convertUtcToLocal,
  formattedTimeDiff,
  getNowInTimezone,
} from './time-utils';
import { convertCentsToDollar } from './utils';

/**
 * Sorts items by created_at in ascending order (oldest first).
 * Items without created_at are placed at the end.
 */
export const sortByCreatedAt = <T extends { created_at?: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => {
    if (!a.created_at && !b.created_at) return 0;
    if (!a.created_at) return 1;
    if (!b.created_at) return -1;
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });

export const calculateElapsedTime = (appointment: TripSource) => {
  const isActive = appointment?.is_active;
  switch (appointment?.status?.value) {
    case 'paused':
      if (appointment?.total_labor_time_in_sec && isActive && appointment?.work_paused_at) {
        return formattedTimeDiff(appointment?.total_labor_time_in_sec || 0);
      }
      break;
    case 'working': {
      // calculate elapsed time difference if trip is resumed while trip is working
      if (appointment?.work_resumed_at && isActive) {
        const currentTime = dayjs();
        const elapsedTimer = currentTime.diff(appointment?.work_resumed_at, 's');
        const totalTimeInSec = Number(appointment?.total_labor_time_in_sec || 0) + elapsedTimer;
        return formattedTimeDiff(totalTimeInSec);
      }
      // calculate elapsed time in end trip if trip is not at all resumed
      if (isActive && appointment?.schedule_start_time && !appointment?.total_labor_time_in_sec) {
        return calculateTimeDiff(
          convertUtcToLocal<AppDate>(appointment?.schedule_start_time || '') as AppDate,
          dayjs(),
        );
      }
      break;
    }
    case 'ended':
      if (
        appointment?.schedule_start_time &&
        appointment?.schedule_end_time &&
        appointment?.total_labor_time_in_sec
      ) {
        // API-supplied value → adapter parse, not raw dayjs(): the raw call keeps
        // dayjs's silent out-of-range rollover ('2026-13-45' → a valid 2027 date) and
        // throws a RangeError from toISOString() on garbage. parseLenient returns null
        // for both, so the branch falls through to the '00:00:00' default.
        const startedAt = parseLenient(appointment?.schedule_start_time);
        if (startedAt) {
          const endedAt = startedAt
            .add(appointment.total_labor_time_in_sec, 'seconds')
            .toISOString();
          return calculateTimeDiff(
            convertUtcToLocal<AppDate>(appointment?.schedule_start_time) as AppDate,
            convertUtcToLocal<AppDate>(endedAt) as AppDate,
          );
        }
      }
      break;
    default:
      break;
  }
  return '00:00:00';
};

export const doAgendaListSorting = (
  agendaList: ListObj[],
  blockedTimeSource: { [key: string]: CalendarObj },
) => {
  let sortedArray: ListObj[] = [];
  const updatedSource = agendaList?.map((agenda) => ({
    ...agenda,
    start_time: blockedTimeSource?.[agenda?.technicianAppointmentId]?.start_time,
  }));

  if (updatedSource?.length) {
    // moment(value, ISO_8601) yielded an OBJECT whose valueOf() is NaN for missing/
    // invalid inputs, and lodash's comparator treats such reflexive objects as
    // equal-to-everything (original order preserved — golden-pinned). An invalid
    // AppDate reproduces that exactly; a bare NaN number would be sorted to the end.
    sortedArray = orderBy(
      updatedSource,
      (data) => parseStrictIsoLocal(data?.start_time ?? '') ?? dayjs(Number.NaN),
      ['asc'],
    );

    sortedArray?.forEach((item: any) => {
      unset(item, 'start_time');
    });
  }
  return sortedArray;
};

export const calculateQuoteEstimatedCost = (quoteTask: QuoteOption[]): number => {
  if (!quoteTask || quoteTask.length === 0) return 0;

  return (
    quoteTask?.reduce((acc, item) => {
      const laborCost = (Number(item?.labor_cost_in_cents) || 0) / 100;
      const laborMarkupFee =
        item?.markup_fee_type === 'percentage'
          ? ((Number(item?.markup_fee_type_value) ?? 0) / 100) * laborCost
          : Number(item?.markup_fee_type_value || 0);

      const totalMaterialCost =
        item?.materials?.reduce((materialAcc, curr) => {
          const unitPrice = (curr?.unit_price_in_cents ?? 0) / 100;
          const quantity = curr?.quantity ?? 0;
          const materialCostPerUnit = unitPrice * quantity;
          const materialCost =
            curr?.cost_type === 'per-unit'
              ? materialCostPerUnit
              : (Number(curr?.material_cost_in_cents) ?? 0) / 100;
          return materialAcc + materialCost;
        }, 0) || 0;

      const materialMarkupFee = Number(
        item?.materials?.reduce((materialMarkupFeeAcc, curr) => {
          const materialCost =
            curr?.cost_type === 'per-unit'
              ? (Number(curr?.unit_price_in_cents || 0) / 100) * Number(curr?.quantity || 0)
              : Number(curr?.material_cost_in_cents || 0) / 100;

          const materialMarkupFeeAmount =
            curr?.markup_fee_type === 'percentage'
              ? (Number(curr?.markup_fee_type_value || 0) / 100) * materialCost
              : Number(curr?.markup_fee_type_value || 0);

          return materialMarkupFeeAcc + materialMarkupFeeAmount;
        }, 0) || 0,
      );

      return acc + laborCost + laborMarkupFee + totalMaterialCost + materialMarkupFee;
    }, 0) || 0
  );
};

export const multipleCategories = (categories?: Array<{ label?: string }>, length?: number) => {
  const result = categories?.slice(0, length || 2)?.map((item) => item?.label);
  return result?.join(', ');
};

// This function processes the trips data
// From the WorkOrder Details and WorkOrder Listing API response
export const processTrips = (tripsData: WorkOrderAPIResponseObj['trips'] = []): WorkOrderTrips => {
  const initialResult: WorkOrderTrips = {
    trip_ids: [],
    tripSource: {},
    activeTripId: '',
  };

  if (!tripsData?.length) return initialResult;

  return tripsData.reduce(
    (result, tripItem) => {
      if (!tripItem?.trip_id) return result;

      // Only add trip_id if it doesn't already exist
      if (!result.tripSource[tripItem.trip_id]) {
        result.trip_ids.push(tripItem.trip_id);
        result.tripSource[tripItem.trip_id] = tripItem;

        // Set activeTripId only if it's not already set and trip is active
        if (tripItem.is_active && !result.activeTripId) {
          result.activeTripId = tripItem.trip_id;
        }
      }

      return result;
    },
    { ...initialResult },
  ); // Ensure we start with a fresh copy
};

export const getWorkOrderStatus = (
  status: WorkOrderDataType['status'],
  trips?: WorkOrderDataType['trips'],
  issues?: WorkOrderDataType['work_order_issues'],
) => {
  // Check if any quote has 'approved' status
  const hasApprovedQuotes =
    issues?.some((issue) => issue?.quotes?.some((quote) => quote.state === 'approved')) || false;
  // ORI-614: evaluate the rejection lock-out at WORK-ORDER level. A rejected quote spans every
  // issue it quoted, so an individual issue can legitimately still hold only the rejected quote
  // while a live replacement quote exists elsewhere on the work order — a per-issue predicate
  // would re-lock the vendor out mid-rebuild.
  const hasRejectedQuotes = hasOnlyRejectedQuotes(
    (issues ?? []).flatMap((issue) => issue?.quotes ?? []),
  );
  if (status.value === WO_STATUS_KEYS.PAUSED) {
    if (hasApprovedQuotes) {
      return {
        ...status,
        value: WO_STATUS_KEYS.SCHEDULE_RETURN_TRIP,
        label: WO_STATUS_LABELS.PAUSED,
      };
    }
    if (hasRejectedQuotes) {
      return {
        ...status,
        value: WO_STATUS_KEYS.COMPLETED,
        label: WO_STATUS_LABELS.COMPLETED,
      };
    }
  }

  if (status.value === WO_STATUS_KEYS.WORK_IN_PROGRESS) {
    if (trips && trips.length > 0) {
      if (trips[0].status.value === WO_STATUS_KEYS.EN_ROUTE) {
        return trips[0].status;
      }
      if (trips[0].status.value === WO_STATUS_KEYS.EN_ROUTE_PAUSED) {
        return {
          ...trips[0].status,
          label: WO_STATUS_LABELS.EN_ROUTE_PAUSED,
          value: WO_STATUS_KEYS.EN_ROUTE_PAUSED,
        };
      }
      if (trips[0].status.value === WO_STATUS_KEYS.PAUSED) {
        return {
          ...trips[0].status,
          label: WO_STATUS_LABELS.IN_PROGRESS_PAUSED,
          value: WO_STATUS_KEYS.IN_PROGRESS_PAUSED,
        };
      }
    }
    return {
      ...status,
      value: WO_STATUS_KEYS.IN_PROGRESS,
      label: WO_STATUS_LABELS.IN_PROGRESS,
    };
  }
  return status;
};

export const getWorkOrderIssueStatus = (issue: WorkOrderIssueType) => {
  let { status } = issue;
  if (status.value === ISSUES_STATUS.ASSIGNED) {
    if (issue?.trips && issue.trips[0].status.value === ISSUES_STATUS.DONE) {
      status = {
        label: ISSUES_STATUS_LABEL.DONE,
        value: ISSUES_STATUS.DONE,
      };
    } else {
      status = {
        label: ISSUES_STATUS_LABEL.PENDING,
        value: ISSUES_STATUS.PENDING,
      };
    }
  } else if (status.value === ISSUES_STATUS.QUOTED) {
    if (issue?.is_approved_in_quote) {
      status = {
        label: ISSUES_STATUS_LABEL.PENDING,
        value: ISSUES_STATUS.PENDING,
      };
    } else {
      status = {
        label: ISSUES_STATUS_LABEL.QUOTED,
        value: ISSUES_STATUS.QUOTED,
      };
    }
  }
  return status;
};

export const getQuoteStatus = (quote: QuoteType) => {
  const { status } = quote;
  if (status.value === QUOTE_STATUS_KEYS.PENDING_REVIEW) {
    return {
      ...status,
      label: QUOTE_STATUS_LABELS.PENDING_APPROVAL,
    };
  }
  return status;
};

/**
 * ORI-614: the vendor portal used to target `quotes[0]` positionally. A vendor receives every
 * quote on the work order including Rejected ones (the backend only filters Drafts for
 * non-vendors) and `quotes` has no guaranteed order, so `quotes[0]` can be the rejected quote —
 * every write then targets a quote the backend refuses (422 invalidQuote).
 *
 * The predicate is exclusion-only (is-rejected) rather than an allow-list of active states: the
 * backend's active set is Draft | PendingReview | PendingApproval | Approved, so an allow-list
 * would have to be kept in lockstep and would break the Approved case the moment a state is added.
 *
 * BOTH discriminators are checked because `state` and `status.value` are spread-merged over a
 * stored quote independently by updateWorkOrderQuotes, from payloads that do not always carry both
 * keys — the vendor broadcast carries both, the coordinator's reject response carries only
 * `status` — so either one can be the stale field.
 */
export const isQuoteRejected = (quote?: QuoteType | null): boolean =>
  quote?.state === QUOTE_STATUS_KEYS.REJECTED ||
  quote?.status?.value === QUOTE_STATUS_KEYS.REJECTED;

export const isQuoteEditable = (quote?: QuoteType | null): boolean => {
  const editableStates: string[] = [QUOTE_STATUS_KEYS.DRAFT, QUOTE_STATUS_KEYS.PENDING_REVIEW];
  const status: string | undefined = quote?.status?.value ?? quote?.state;
  return status !== undefined && editableStates.includes(status);
};

const getQuoteNumber = (quote: QuoteType): number => quote.quote_no ?? quote.quote_number ?? 0;

/**
 * The quote every WRITE must target: the NEWEST non-rejected quote, or undefined if there is none.
 *
 * Ordered by quote number rather than array position because `quotes` is an unordered hasMany, and
 * more than one non-rejected quote is reachable (a second quote is allowed once the previous trip
 * is no longer active) — positional selection could target an already-approved quote instead of
 * the draft the vendor is building. Quotes carry `quote_no` at work-order level and
 * `quote_number` at issue level; when neither is present the sort is stable, i.e. array order.
 */
export const getActiveQuote = (quotes?: QuoteType[] | null): QuoteType | undefined =>
  (quotes ?? [])
    .filter((quote) => !isQuoteRejected(quote))
    .sort((first, second) => getQuoteNumber(first) - getQuoteNumber(second))
    .at(-1);

/**
 * The work-order-level active quote. Additionally ignores quotes that carry no loaded task list.
 *
 * `updateWorkOrderQuotes` no longer appends an unknown realtime frame to `source.quotes` — it
 * flags the panel for a refetch instead — so the phantom-quote path this filter was written for
 * is closed at the source. The filter stays as the invariant it always encoded: a quote with no
 * `tasks` is a summary, not a record, and must never be selected as the write target. Selecting
 * one would submit the wrong quote on close (erasing that quote's rejection record) and deref a
 * missing task list. A vendor's own work-order payload always carries `tasks`, so nothing real
 * is excluded.
 */
export const getActiveWorkOrderQuote = (quotes?: QuoteType[] | null): QuoteType | undefined =>
  getActiveQuote((quotes ?? []).filter((quote) => Array.isArray(quote.tasks)));

/** True only when quotes exist AND all of them are rejected. */
export const hasOnlyRejectedQuotes = (quotes?: QuoteType[] | null): boolean =>
  (quotes?.length ?? 0) > 0 && !getActiveQuote(quotes);

/**
 * The quote READ-ONLY surfaces should render: the active quote when there is one, otherwise the
 * last quote so a rejected-only issue still shows its rejected quote (card, options, pricing,
 * rejection banner) instead of blanking out.
 */
export const getDisplayQuote = (quotes?: QuoteType[] | null): QuoteType | undefined =>
  getActiveQuote(quotes) ?? quotes?.at(-1);

export const calculateQuoteTaskOptionTotal = (
  laborCost: number,
  materials?: IssueMaterialType[],
): number => {
  if (laborCost) {
    const hasMaterials = materials && materials?.length > 0;
    const totalMaterialsCost = () => {
      if (!hasMaterials) return 0;
      return materials.reduce((acc: any, item: any) => {
        const quantity: number = item?.quantity || 0;
        // Convert cents to dollars using utility function
        const originalCostInDollars: number = convertCentsToDollar(item?.cost_in_cents || 0);
        let itemTotalCost: number;
        if (item.cost_type === 'total') {
          itemTotalCost = originalCostInDollars;
        } else {
          itemTotalCost = originalCostInDollars * quantity;
        }
        return acc + itemTotalCost;
      }, 0);
    };
    const total = () => {
      // laborCost is already in dollars
      const actualCost: number = Number(laborCost);
      return totalMaterialsCost() + Number(actualCost);
    };
    return total() || 0;
  }
  return 0;
};
export const calculateQuoteSumTotal = (tasks: QuoteTaskOptionType[]): number[] => {
  if (tasks) {
    const sum = tasks?.map((task: QuoteTaskOptionType) => {
      const { laborCost, materials } = task;
      return calculateQuoteTaskOptionTotal(laborCost || 0, materials || []);
    });
    return sum;
  }
  return [0];
};

/** Re-export for callers that import from work-order-utils. */
export type { ServiceWindow } from './schedule-types';

/**
 * Builds a list of selectable service windows (date + time slots) for reschedule/schedule flows.
 * Use when rendering the "Select Schedule" options in the vendor portal (reschedule, schedule return trip).
 *
 * Slots are 8–12, 12–16, 16–20 in the given zone; start/end are returned as UTC ISO so the API
 * stores correct times. Pass the work order timezone so the UI and the payload match the job's
 * location.
 *
 * @param technicianUUID - Required; used on each window for API/assignment. Empty → [].
 * @param daysAhead - Number of days to generate (default 14). Day 0 = today; elapsed slots on today are excluded.
 * @param timezone - REQUIRED IANA timezone (e.g. "America/Chicago"). Absent, malformed or a bare
 *   UTC offset throws — see the guard below for why there is no fallback here.
 * @returns Array of ServiceWindow with startTime/endTime as UTC ISO strings.
 * @throws If `timezone` is not a valid IANA zone.
 */
export const generateServiceWindows = (
  technicianUUID: string,
  daysAhead: number = 14,
  timezone: string,
): ServiceWindow[] => {
  if (!technicianUUID) return [];

  // APX-567: absent or malformed zones used to fall back to the browser's local zone
  // here, silently shifting windows that are persisted as UTC ISO.
  if (!isValidTimezone(timezone)) {
    throw new Error('generateServiceWindows: a valid IANA timezone is required');
  }

  const nowInTz = getNowInTimezone(timezone);
  // Plain-local calendar-day cursor: day arithmetic stays date-based, and each
  // slot's wall-clock instant is rebuilt in the target zone via wallClockAtHour
  // (DST-safe — naive hour()/add() on a tz instance keeps the pre-transition offset).
  const startOfToday = dayjs(nowInTz.format('YYYY-MM-DD'));

  const windows: ServiceWindow[] = [];

  for (let day = 0; day < daysAhead; day += 1) {
    const dayCursor = startOfToday.add(day, 'day');

    for (const slot of SCHEDULE_SLOT_DEFINITIONS) {
      const slotStart = wallClockAtHour(dayCursor, slot.startHour, timezone);
      const slotEnd = wallClockAtHour(dayCursor, slot.endHour, timezone);

      if (day === 0 && slotEnd.isSameOrBefore(nowInTz)) continue;

      windows.push({
        technicianUUID,
        startTime: slotStart.toISOString(),
        endTime: slotEnd.toISOString(),
        onHours: true,
      });
    }
  }

  return windows;
};

// For quote task issues, top-level `materials` is always empty from the API — real materials
// live in `quote_task.options[selected_option].materials`. Falls back to top-level if no quote task.
export const resolveIssueMaterials = <T>(
  materials: T[] | undefined,
  quote_task:
    | { selected_option?: string; options?: Array<{ option_id: string; materials?: unknown[] }> }
    | null
    | undefined,
): T[] => {
  const selectedOption = quote_task?.options?.find(
    (opt) => opt.option_id === quote_task?.selected_option,
  );
  if (selectedOption?.materials?.length) return selectedOption.materials as T[];
  if (materials?.length) return materials;
  return materials ?? [];
};

export const checkAllIssuesMarked = (issues: WorkOrderIssueType[]) =>
  issues.every(
    (issue: WorkOrderIssueType) =>
      issue.status.value !== ISSUES_STATUS.PENDING && issue.status.value !== ISSUES_STATUS.ASSIGNED,
  );

export const formatWorkOrderTasks = (tasks: QuoteTaskType[], _issues?: WorkOrderIssueType[]) =>
  tasks.map((task: QuoteTaskType) => ({
    id: task.quote_task_id,
    issueId: task.work_order_issue_id,
    options: task.options.flatMap((option: TaskOptionType) => ({
      id: option.option_id,
      description: option.description,
      optionType: option.option_type,
      medias: option.media,
      laborCost: convertCentsToDollar(option.labor_cost_in_cents),
      materials: option.materials.flatMap((material: MaterialType) => ({
        id: material.material_id,
        label: material.label,
        quantity: material.quantity,
        material_cost: convertCentsToDollar(material.cost_in_cents || 0),
        cost_type: material.cost_type,
        quantity_type: MATERIAL_QUANTITY_TYPES[material.quantity_type],
        total_cost: convertCentsToDollar(material.total_cost_in_cents),
      })),
      materialsCost: convertCentsToDollar(
        option.materials.reduce((t: number, m: MaterialType) => t + m.total_cost_in_cents, 0),
      ),
      totalCost: convertCentsToDollar(option.total_cost_in_cents),
    })),
    taskType: task.task_type,
    title: task.title,
  }));

export const convertMaterialTypeToFormType = (material?: MaterialType) => ({
  id: material?.material_id || '',
  label: material?.label || '',
  quantity: material?.quantity || 0,
  material_cost: convertCentsToDollar(
    (material?.cost_type === 'per-unit'
      ? material?.unit_price_in_cents
      : material?.cost_in_cents) || 0,
  ),
  cost_type: material?.cost_type || 'total',
  quantity_type: MATERIAL_QUANTITY_TYPES[material?.quantity_type || 'units'],
  total_cost: convertCentsToDollar(material?.total_cost_in_cents || 0),
});

export const convertTaskOptionToFormType = (option: TaskOptionType) => ({
  id: option.option_id,
  description: option.description,
  optionType: option.option_type,
  medias: option.media,
  laborCost: convertCentsToDollar(option.labor_cost_in_cents),
  materials:
    option.materials?.map((material: MaterialType) => convertMaterialTypeToFormType(material)) ||
    [],
  materialsCost: convertCentsToDollar(
    option.materials?.reduce((t: number, m: MaterialType) => t + m.total_cost_in_cents, 0) || 0,
  ),
  totalCost: convertCentsToDollar(option.total_cost_in_cents),
});

export const addWorkOrderTrip = (trips: TripType[], newTrip: TripType): TripType[] => [
  {
    ...newTrip,
    is_active: true,
    trip_number: trips.length + 1,
  },
  ...trips.map((trip, index) => {
    if (index === 0) {
      return {
        ...trip,
        is_active: false,
      };
    }
    return trip;
  }),
];

export const updateWorkOrderTrip = (
  trips: TripType[],
  tripId: string,
  data: Partial<TripType>,
): TripType[] => {
  const index = trips.findIndex((trip: TripType) => trip.trip_id === tripId);

  if (index === -1) {
    return trips;
  }

  const updatedTrips = [...trips];
  updatedTrips[index] = {
    ...trips[index],
    ...data,
  };

  return updatedTrips;
};

export const updateWorkOrderIssue = (
  issues: WorkOrderIssueType[],
  issueId: string,
  data: Partial<WorkOrderIssueType>,
): WorkOrderIssueType[] => {
  const updatedIssues = issues.map((issue: WorkOrderIssueType) => {
    if (issue.issue_id === issueId) {
      return { ...issue, ...data };
    }
    return issue;
  });

  return updatedIssues;
};

export const updateWorkOrderIssueStatus = (
  issues: WorkOrderIssueType[],
  issueId: string,
  data: Partial<WorkOrderIssueType>,
  _status: OptionType,
): { allIssuesMarked: boolean; issues: WorkOrderIssueType[] } => {
  // Update the issue using the general update function
  const updatedIssues = updateWorkOrderIssue(issues, issueId, data);

  // Check if all issues are marked (not pending or assigned)
  const allIssuesMarked = checkAllIssuesMarked(updatedIssues);

  return {
    allIssuesMarked,
    issues: updatedIssues,
  };
};

/**
 * The issue's selected quote-task option as it arrives on the issue payload. Declared locally
 * (and NOT added to `WorkOrderIssueType`) because `quote_task` currently reaches this helper
 * through that type's `[x: string]: any` index signature; declaring it there would need an
 * escape hatch that re-admits `any` across every existing read site.
 */
type SelectedQuoteTaskOption = { option_id?: string | null; option_type?: QuoteWorkType | null };

/**
 * Outcome of resolving an issue's work type ("repair" / "replace").
 *
 * ORI-637: composition used to fail open to the bare title with no diagnostic, so
 * "this issue genuinely has no work type" was indistinguishable from "the lookup failed".
 * The discriminated union makes callers state which case they are in.
 */
export type IssueWorkTypeResult =
  | { workType: QuoteWorkType; source: 'quote-lookup' | 'issue-payload' }
  | { workType: null; reason: 'no-selected-option' | 'unresolved-option' };

/**
 * The single declaration of the work types and the labels they render as.
 *
 * Typed as `Record<QuoteWorkType, string>` deliberately: that is an EXACT-SET check, so widening
 * `QuoteWorkType` without adding an entry here fails the build. (An array with
 * `as const satisfies readonly QuoteWorkType[]` does NOT give that guarantee — `satisfies` is only
 * an assignability check, so `readonly ['repair', 'replace']` stays assignable to a wider union and
 * a third member would compile clean, then silently fall through `isQuoteWorkType` to a bare
 * title.) The runtime allowlist and the strip regex below are both derived from this map so the
 * three cannot drift apart.
 */
const WORK_TYPE_LABELS: Record<QuoteWorkType, string> = {
  repair: 'Repair',
  replace: 'Replace',
};

const WORK_TYPE_VALUES = Object.keys(WORK_TYPE_LABELS);

/**
 * Runtime allowlist. `option_type` crosses an `any` boundary (see `SelectedQuoteTaskOption`),
 * so a truthiness check alone would happily render `(Refurbish)` to a vendor for any rogue string.
 */
const isQuoteWorkType = (value: unknown): value is QuoteWorkType =>
  typeof value === 'string' && WORK_TYPE_VALUES.includes(value);

/**
 * Matches a TRAILING RUN of already-composed work-type suffixes. Derived from `WORK_TYPE_LABELS`;
 * end-anchored, so genuine human text such as "Unit Needs (Minor Repair)" is not eaten (the
 * parenthetical must match a label EXACTLY). The `+` makes the fix self-healing: a title that the
 * pre-ORI-637 code already double-suffixed ("Clogged Toilet (Repair) (Replace)") collapses back to
 * a single correct suffix instead of keeping the stale inner one forever.
 *
 * The separator is `\s*` with a trailing `\s*` allowed, NOT a single literal space. It is OPTIONAL
 * rather than required because `\s+` left two duplication classes open: "Clogged Toilet(Repair)"
 * gained a second suffix, and a title that is exactly "(Repair)" composed "(Repair) (Replace)" —
 * the very class the emptiness guard below claims to close. A hard-coded
 * " (" only recognised this function's own exact output, so a title carrying a NON-BREAKING space
 * (a routine artefact of text pasted from Word/Docs/PDF) or a trailing space escaped the strip and
 * got a SECOND suffix appended: "Clogged Toilet (Repair)" -> "Clogged Toilet (Repair) (Repair)".
 * `\s` matches U+00A0 in JS, so both cases now normalise. Label values are escaped because they are
 * interpolated into a pattern — a future label containing a regex metacharacter would otherwise
 * change this pattern's meaning instead of failing loudly.
 */
const COMPOSED_WORK_TYPE_SUFFIX = new RegExp(
  // `String.raw` so the pattern reads as written rather than double-escaped, and an explicit arrow
  // rather than `.map(escapeRegExp)` — `.map` passes (value, index, array), and handing a function
  // reference extra positional arguments is the `.map(parseInt)` footgun even where, as here, the
  // callee happens to ignore them.
  String.raw`(?:\s*\((?:${Object.values(WORK_TYPE_LABELS)
    .map((label) => escapeRegExp(label))
    .join('|')})\))+\s*$`,
);

/**
 * Resolves the work type for an issue from its selected quote-task option.
 *
 * ORI-637: deliberately does NOT read `is_approved_in_quote`. The backend forces that flag to
 * `false` while the latest trip pivot status is "Quoted" (it also gates the MARK_AS_DONE
 * ability), which made the work-type suffix disappear from the Quote Task Details header.
 */
export const resolveIssueWorkType = (
  issue: WorkOrderIssueType,
  quotes?: QuoteType[] | null,
): IssueWorkTypeResult => {
  const selectedOption: SelectedQuoteTaskOption | null | undefined =
    issue?.quote_task?.selected_option;
  const selectedOptionId = selectedOption?.option_id;
  if (!selectedOptionId) return { workType: null, reason: 'no-selected-option' };

  // Look the option up in `quotes` FIRST — this ordering is an invariant, not style.
  // `scheduleVendorReturnTrip().fulfilled` patches `selected_option.option_id` while spreading
  // the OLD `selected_option`, so the payload's own `option_type` is one trip stale there;
  // payload-first would render the WRONG work type.
  const matchingOptionType = (quotes ?? [])
    .flatMap((quote) => quote.tasks || [])
    .flatMap((task) => task.options || [])
    .find((option) => option.option_id === selectedOptionId)?.option_type;
  if (isQuoteWorkType(matchingOptionType)) {
    return { workType: matchingOptionType, source: 'quote-lookup' };
  }

  const payloadOptionType = selectedOption?.option_type;
  if (isQuoteWorkType(payloadOptionType)) {
    return { workType: payloadOptionType, source: 'issue-payload' };
  }

  return { workType: null, reason: 'unresolved-option' };
};

/**
 * Composes the issue title with its work-type suffix, e.g. "Clogged Toilet (Repair)".
 *
 * `quotes` is optional/nullable because `state.source` initialises as `{}`, so the reducers really
 * can call this with `undefined` — the resolver already tolerates it.
 *
 * The return stays annotated `string` even though the falsy-title path can propagate `undefined`.
 * Review on #1219 flagged that dishonesty and it is real, but the fix does NOT belong here:
 * `WorkOrderIssueType.title` is itself declared `string` (vendor-portal/work-order/types.ts:198) and
 * both callers assign this result straight into that field, so widening only this signature just
 * moves the lie one level out and fails the build. Correcting it means widening the store type and
 * auditing its consumers — deliberately left as a follow-up rather than smuggled in here.
 */
export const getWorkOrderIssueTitle = (
  issue: WorkOrderIssueType,
  quotes?: QuoteType[] | null,
): string => {
  const issueTitle: string = issue?.title;
  const workTypeResult = resolveIssueWorkType(issue, quotes);

  if (workTypeResult.workType === null) {
    // ORI-654: the unresolved-option case is intentionally NOT logged here. Both callers of this
    // helper are Redux reducers (Immer producers), so logging here is a side effect executing
    // inside a pure reducer. The diagnostic now lives at the view boundary
    // (useWorkTypeDiagnostics), which reads the same resolveIssueWorkType result via
    // selectUnresolvedWorkTypeIssues and applies a throttling policy there. This helper stays
    // pure — it composes the title and nothing else.
    return issueTitle;
  }

  // DELIBERATE BEHAVIOUR CHANGE (ORI-637): a falsy title is returned as-is. The pre-ORI-637 code
  // appended unconditionally once an option matched, so a wire payload with `title: ''` produced
  // the orphan title `' (Repair)'` — a suffix with nothing to qualify. Returning '' is both safer
  // and visually inert (the card renders `base_title` and the panel header renders
  // `issue?.title || title`, so both operands are '' either way).
  // NOTE: this removes the reducer-reachable half of the ORI-620 `??`-not-`||` discriminator —
  // the reducers can no longer produce `{ base_title: '', title: ' (Repair)' }`. That guard is
  // still required and is still pinned by a now-deliberately-synthetic fixture in
  // tests/unit/VendorPortal/.../QuoteApprove/OriginalIssue.test.tsx. Do not prune it.
  if (!issueTitle) return issueTitle;

  // Strip-then-append keeps this idempotent: the stored title is re-derived on later actions, and
  // an opposite-type return trip previously produced "Clogged Toilet (Repair) (Replace)".
  //
  // ACCEPTED AMBIGUITY: an author-written title whose ending matches a label EXACTLY is
  // indistinguishable from this function's own output, so it is rewritten to the resolved work
  // type. That is the unavoidable cost of idempotent re-derivation, and the resolved quote option
  // is the more authoritative of the two. This covers BOTH the spaced form ("Faucet (Replace)" ->
  // "Faucet (Repair)") and, since the separator became optional, the ATTACHED form
  // ("AC-Unit-3000(Replace)" -> "AC-Unit-3000 (Repair)"), which additionally normalises the missing
  // space. Both are pinned by test, so this stays a decision rather than a discovery. The
  // exact-label requirement keeps near-misses safe: "(Minor Repair)" and "(Repairs)" are untouched.
  //
  // Append from the TRAILING-trimmed base, not the raw stripped value. When no suffix is present
  // there is nothing for the pattern's trailing `\s*` to consume, so a title with trailing
  // whitespace kept it and gained a second separator ("Clogged Toilet " ->
  // "Clogged Toilet  (Repair)"). That also broke idempotence outright, since re-deriving over the
  // result then collapsed the run: f(f(x)) !== f(x) for every trailing-whitespace title.
  //
  // `trimEnd`, NOT `trim`: this function's remit is the suffix and the separator in front of it, so
  // it has no business normalising LEADING whitespace. A full trim would also make normalisation
  // conditional on resolution state — `base_title` keeps the raw wire value and the unresolved arm
  // returns the raw title, so the Original Issue card would show leading whitespace where the
  // header had silently dropped it. Leading whitespace is preserved and pinned by test.
  const composedBase = issueTitle.replace(COMPOSED_WORK_TYPE_SUFFIX, '').trimEnd();

  // Decide on the STRIPPED value, not the raw one. Guarding the raw title let a title that is
  // ENTIRELY a suffix run (" (Repair)") regenerate the exact orphan suffix the guard exists to
  // prevent — it is truthy, so it passed, then stripped to "" and composed to " (Replace)". This
  // also covers a whitespace-only wire title, which previously produced "  (Repair)".
  if (!composedBase) return '';

  return `${composedBase} (${WORK_TYPE_LABELS[workTypeResult.workType]})`;
};

/**
 * Health classification values used in Work Order health score system
 */
export type HealthClassification = 'healthy' | 'at_risk' | 'critical';

/**
 * Attributes received from the backend health score Pusher event
 */
export type WorkOrderHealthAttributes = {
  from: HealthClassification;
  to: HealthClassification;
  violation: string | null;
  violation_slug: string | null;
  condition: string;
  health_tracker_uuid: string;
  duration: number; // hours elapsed since violation started
  startDate: string; // ISO-8601 timestamp
  endDate: string; // ISO-8601 timestamp
};

/**
 * Pusher payload for Work Order Health Score events (ORI-69)
 * Event names: workorder.health.at_risk, workorder.health.critical, workorder.health.healthy
 */
export type WorkOrderHealthPusherPayload = {
  data: {
    work_order_id: string;
    attributes: WorkOrderHealthAttributes;
    created_at: string;
  };
};

/**
 * Result of transforming a health score Pusher payload
 */
export type TransformedHealthScoreResult = {
  workOrderId: string;
  healthScore: HealthScore;
};

/**
 * Transforms a Pusher health score payload into the frontend HealthScore format.
 * This ensures backward compatibility with existing UI components that expect:
 * - classification: the health status (at_risk, critical, healthy)
 * - elapse_time_in_sec: ISO timestamp used by useElapsedTime hook to calculate elapsed time
 * - slug: the violation slug for identifying the type of violation
 *
 * @param payload - The Pusher event payload for health score changes
 * @returns HealthScore object compatible with existing frontend components, or null if invalid
 */
export const transformHealthScorePusherPayload = (
  payload: WorkOrderHealthPusherPayload,
): TransformedHealthScoreResult | null => {
  if (!payload?.data?.work_order_id || !payload?.data?.attributes) {
    return null;
  }

  const { work_order_id, attributes, created_at } = payload.data;

  return {
    workOrderId: work_order_id,
    healthScore: {
      classification: attributes.to,
      // startDate is the ISO timestamp when the violation started
      // useElapsedTime hook uses this to calculate elapsed time from now
      elapse_time_in_sec: attributes.startDate,
      slug: attributes.violation_slug ?? undefined,
      created_at,
    },
  };
};
